import { BloodworkAnalysis, Section } from '../models';

export interface Supplement {
  id: string;
  name: string;
  brand: string;
  description: string;
  link?: string;
}

export interface SupplementRecommendation {
  supplement: Supplement;
  reasons: string[];
}

// Mock database of supplements pending future DynamoDB integration
const supplementsDB: Supplement[] = [
  {
    id: 'sys-immuno-1',
    name: 'Immuno-Shield',
    brand: 'systemicFormulas',
    description: 'A comprehensive immune support formula.',
  },
  {
    id: 'sys-iron-1',
    name: 'Iron Plus',
    brand: 'systemicFormulas',
    description: 'Highly bioavailable iron supplement.',
  },
  {
    id: 'sys-lipid-1',
    name: 'Lipid Balance',
    brand: 'systemicFormulas',
    description: 'Supports healthy cholesterol levels.',
  },
];

export class SupplementsEngine {
  static getRecommendations(
    section: Section,
    bloodworkAnalysis: BloodworkAnalysis,
    enabledBrands: string[],
  ): SupplementRecommendation[] {
    if (!enabledBrands || enabledBrands.length === 0) {
      return [];
    }

    const recommendations: SupplementRecommendation[] = [];

    // Helper to evaluate a rule and add recommendation if passed
    const addIf = (
      condition: boolean,
      supplementId: string,
      reason: string,
    ) => {
      if (condition) {
        const supp = supplementsDB.find(
          s => s.id === supplementId && enabledBrands.includes(s.brand),
        );
        if (supp) {
          // Check if already recommended
          const existing = recommendations.find(
            r => r.supplement.id === supp.id,
          );
          if (existing) {
            existing.reasons.push(reason);
          } else {
            recommendations.push({ supplement: supp, reasons: [reason] });
          }
        }
      }
    };

    // Evaluate rules based on the section
    switch (section) {
      case Section.WBC:
        if (bloodworkAnalysis.wbc && bloodworkAnalysis.wbc.hasValue()) {
          addIf(
            bloodworkAnalysis.wbc.value <
              bloodworkAnalysis.wbc.range.optimalMin,
            'sys-immuno-1',
            `WBC is below optimal (${bloodworkAnalysis.wbc.value})`,
          );
          addIf(
            bloodworkAnalysis.wbc.value >
              bloodworkAnalysis.wbc.range.optimalMax,
            'sys-immuno-1',
            `WBC is above optimal (${bloodworkAnalysis.wbc.value})`,
          );
        }
        break;

      case Section.IRON:
      case Section.RBC:
        if (bloodworkAnalysis.iron && bloodworkAnalysis.iron.hasValue()) {
          addIf(
            bloodworkAnalysis.iron.value <
              bloodworkAnalysis.iron.range.optimalMin,
            'sys-iron-1',
            `Iron is below optimal (${bloodworkAnalysis.iron.value})`,
          );
        }
        break;

      case Section.LIPIDS:
        if (
          bloodworkAnalysis.cholesterol &&
          bloodworkAnalysis.cholesterol.hasValue()
        ) {
          addIf(
            bloodworkAnalysis.cholesterol.value >
              bloodworkAnalysis.cholesterol.range.optimalMax,
            'sys-lipid-1',
            `Cholesterol is above optimal (${bloodworkAnalysis.cholesterol.value})`,
          );
        }
        break;

      // Add more sections and rules as needed for MVP...
    }

    return recommendations;
  }
}
